import { useSession } from './mutations'

/**
 * Whether the signed-in person holds a permission.
 *
 * ── This is not authorisation ────────────────────────────────────────────
 * Every route that matters is checked again by the API on every request, and
 * that check is the real one. This exists to avoid OFFERING a control the server
 * will refuse — a Delete button that always errors is worse than no button, and
 * an empty screen with no explanation is worse than a sentence saying why.
 *
 * Which is why it fails CLOSED on an unknown answer but never hides anything the
 * server would have allowed: a wrong `false` costs somebody a screen they could
 * have used, and a wrong `true` costs a 403 they can read.
 *
 * ── `is_root` bypasses, as it does server-side ───────────────────────────
 * A root user's permission map can be empty and they may still do everything —
 * `require_permission` lets them through without consulting it. Mirrored here so
 * the tenant's first account is not locked out of the screens it exists to set
 * up.
 *
 * ── The map is keyed by permission and valued by SCOPE ───────────────────
 * `{ "users.manage": [{plant_id: …}, …] }` — holding a permission at all is the
 * question this answers, not where. `holdsAny(…, at)` below answers where for one
 * record; until a screen asks it, the screens that would need it (a plant-scoped
 * hierarchy, a line-scoped sensor form) read the API's own refusal instead, which
 * is per-record and cannot drift.
 */
export function usePermission(key) {
  const session = useSession()

  const isRoot = session.data?.user?.isRoot === true
  const held = session.data?.permissions ?? {}

  return {
    /* `isPending` and not `isLoading`: with `initialData` from storage the query
       can be pending while already holding a profile, and gating on the slower
       flag would flash the refusal at somebody who has the permission. */
    isPending: session.isPending && !session.data,
    isError: session.isError,
    granted: isRoot || Object.hasOwn(held, key),
    isRoot,
  }
}

/**
 * Whether a session holds ANY of `keys` — optionally at one place.
 *
 * The one rule the hooks below share, written as a plain function so a table of
 * modules can be filtered with it without a hook per row.
 *
 * `keys` mirrors `require_permission(*keys)`: any one of them is enough. An empty
 * or missing list means the thing is not permission-gated at all (Home, the demo
 * screens), so it is granted.
 *
 * `at` is a record's own location, `{plant_id, line_id, machine_id}`, as every
 * scoped row carries it. Without it the question is "held anywhere". With it,
 * a scope covers the row if it is account-level or names the row's plant, line
 * or machine — the backend's `hierarchy_scope_filter` and `asset_in_scope`, read
 * from the client side. A grant covers what is beneath it, and a row names every
 * ancestor it has, so no tree is needed to answer it.
 *
 * Root holds everything, with an empty map — `require_permission` never consults
 * it for them.
 */
export function holdsAny(session, keys, at) {
  if (!keys?.length) return true
  if (session?.user?.isRoot === true) return true

  const held = session?.permissions ?? {}
  const ids = at ? [at.plant_id, at.line_id, at.machine_id].filter(Boolean) : null

  return keys.some((key) => {
    if (!Object.hasOwn(held, key)) return false
    if (!ids) return true
    return (held[key] ?? []).some(
      (scope) =>
        Boolean(scope?.account_id) ||
        ids.includes(scope?.plant_id) ||
        ids.includes(scope?.line_id) ||
        ids.includes(scope?.machine_id),
    )
  })
}

/**
 * `holdsAny` against the signed-in session, as a function — for filtering a
 * list of nav items or settings pages that each carry their own `requires`.
 *
 * While the session has not answered at all it says yes. The profile is seeded
 * from storage, so this window is only a first-ever sign-in; showing the rail and
 * letting each screen's own check rule is better than a rail that pops in.
 */
export function usePermissionCheck() {
  const session = useSession()
  const known = Boolean(session.data)
  return (keys, at) => (known ? holdsAny(session.data, keys, at) : true)
}

/**
 * The same question, plus WHERE — which the shift rules actually turn on.
 *
 * ── Why this exists beside `usePermission` ───────────────────────────────
 * `/auth/me` serves `{ "shifts.manage": [ {account_id, plant_id, …}, … ] }` — a
 * list of scope rows per permission. `usePermission` throws the value away
 * because, for most screens, holding a permission at all is the whole question.
 *
 * Shifts are the exception. The server distinguishes an ACCOUNT-level grant from
 * a PLANT one and the two can do different things:
 *
 *   an account grant sees, creates, edits and deletes every shift;
 *   a plant grant does all of that for its own plants' shifts, and sees
 *   account-level shifts read-only.
 *
 * (`covers_plant` in the backend's `core/queries/scoping.py` is the one rule;
 * this is the information needed to mirror it.) Offering Delete on a shift the
 * server will refuse to delete is exactly what this avoids.
 *
 * ── Still not authorisation ─────────────────────────────────────────────
 * Same caveat as above: the API rules on every request and that check is the
 * real one. A root user bypasses, as they do server-side — `covers_plant`
 * returns true on empty scopes.
 *
 * @returns {{granted: boolean, isRoot: boolean, accountLevel: boolean, plantIds: Set<string>}}
 */
export function usePermissionScopes(key) {
  const session = useSession()

  const isRoot = session.data?.user?.isRoot === true
  const scopes = session.data?.permissions?.[key] ?? []

  return {
    isPending: session.isPending && !session.data,
    isError: session.isError,
    granted: isRoot || scopes.length > 0,
    isRoot,
    /* An account-level row is the one that covers everything. Root has no rows
       at all and covers everything too. */
    accountLevel: isRoot || scopes.some((scope) => Boolean(scope?.account_id)),
    /* Only the plant rows. A region or sub-region row cannot appear here while
       `shifts.manage` declares `target_level: [account, plant]`, and if one ever
       does it is ignored rather than guessed at — the server's refusal is then
       the honest answer. */
    plantIds: new Set(scopes.map((scope) => scope?.plant_id).filter(Boolean)),
  }
}
